import type { ProjectResponse } from '../../shared/models/projects/ProjectResponse';
import type { AccountResponse } from '../../shared/models/account/AccountResponse';
import type { MonitoringInterval } from '../../shared/models/projects/MonitoringInterval';
import type { ProjectStatus } from '../../shared/types/projects/ProjectStatus';
import type { ProjectListKpi } from '../../shared/api/services/project/stats/ProjectDailyStatsService';
import type { TeamResponse } from '../../shared/models/team/TeamResponse';
import type { ProjectMember } from '@brand24-original/llm-types';
import type { RoleType } from '../../shared/types/user/accounts/RoleType';

/**
 * Row models of the hub's two tables, built from the existing read endpoints (`/projects/page` +
 * `list-kpis`, `/team/data`). Every write the hub offers is live now (pause / start and allocations
 * through `PUT /projects/allocations`, role, scope, share, remove); resending an invitation is not
 * offered until it has an endpoint.
 */

export interface AgencyHubProject {
  id: number;
  name: string;
  domain: string | null;
  /** `project.localization` (a `brightDataCountries` code); null = Global. */
  countryCode: string | null;
  defaultPeriod: string;
  /** From `list-kpis`; null until the KPIs arrive. */
  visibilityPercent: number | null;
  /** `visibilityPercentTrend` from `list-kpis`: percentage points vs the previous 30 days; null without data. */
  visibilityTrend: number | null;
  /** Prompts in use (`monitorStatusCounts.active + pending` — the buckets put every status that is
   *  neither paused nor deleted into `active`, the same reading as the pool counts on the server). */
  used: number;
  /** Prompts a start would resume: the ones the hub's pause parked (not those a user paused on purpose). */
  pausedPrompts: number;
  /**
   * The reservation as the tile counts it: a paused project holds 0 whatever the column says, an active
   * one at least what it uses (the column may lag — the pause / resume paths outside the hub do not
   * touch it yet), a project without an allocation what it uses (the backfill rule).
   */
  allocated: number;
  /**
   * The project's own limit: null = none (it counts only what it uses), 0 for a paused project, else
   * what `allocated` holds. The tables show "no allocation limit" for null instead of a bar.
   */
  allocation: number | null;
  monitoringInterval: MonitoringInterval;
  created: Date;
  status: ProjectStatus;
}

/**
 * The account's own projects out of the context list (`/projects`): that list carries the demo project
 * and, for a superadmin, every account's projects — the hub manages one account. Same rule as the
 * `accountOnly` flag of `/projects/page`. A null account only satisfies the context's type: `Wrapper`
 * holds the page behind a loader until the account and the list are both in, so the empty list is
 * never painted (and an unfiltered fallback could flash another account's projects).
 */
export const ownProjects = (
  accountResponse: AccountResponse | null,
  projects: ProjectResponse[]
): ProjectResponse[] =>
  accountResponse ? projects.filter((project) => project.accountId === accountResponse.accountId) : [];

export const toAgencyHubProject = (project: ProjectResponse, kpi?: ProjectListKpi): AgencyHubProject => {
  const counts = project.monitorStatusCounts ?? {
    active: 0,
    paused: 0,
    resumable: 0,
    pending: 0,
    total: 0,
  };
  const used = counts.active + counts.pending;
  const allocated = project.status === 'PAUSED' ? 0 : Math.max(project.promptAllocation ?? used, used);
  return {
    id: project.id,
    name: project.projectLabel || project.firmName,
    domain: project.domain ?? null,
    countryCode: project.localization,
    defaultPeriod: project.defaultPeriod,
    visibilityPercent: kpi ? kpi.visibilityPercent : null,
    visibilityTrend: kpi ? kpi.visibilityPercentTrend : null,
    used,
    pausedPrompts: counts.resumable ?? 0,
    allocated,
    allocation: project.status === 'ACTIVE' && project.promptAllocation === null ? null : allocated,
    monitoringInterval: project.monitoringInterval ?? { value: 1, unit: 'DAY' },
    created: new Date(project.created),
    status: project.status,
  };
};

/** Active or pending; Expired waits for the invitation lifecycle (no timestamp to derive it from yet). */
export type MemberStatus = 'ACTIVE' | 'PENDING';

export interface AgencyHubMember {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  role: RoleType;
  status: MemberStatus;
  invited: Date;
  /** Shared projects — GUEST only; empty for the other roles. */
  projectIds: number[];
}

export const toAgencyHubMember = (member: TeamResponse): AgencyHubMember => ({
  id: member.id,
  firstName: member.firstName,
  lastName: member.lastName,
  email: member.email,
  role: member.role,
  status: member.isActive ? 'ACTIVE' : 'PENDING',
  invited: new Date(member.invitedAt),
  projectIds: member.projectIds ?? [],
});

/** "First Last", or the e-mail when the member has no name yet (an invitation without one). */
export const memberDisplayName = (member: Pick<ProjectMember, 'firstName' | 'lastName' | 'email'>) =>
  `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim() || member.email;

const byLastName = (a: ProjectMember, b: ProjectMember) =>
  `${a.lastName} ${a.firstName} ${a.email}`.localeCompare(
    `${b.lastName} ${b.firstName} ${b.email}`,
    undefined,
    { sensitivity: 'base', numeric: true }
  );

/**
 * The "Share project" dialog's rows: only the guests (`scope === 'PROJECT'`) — the roles that see
 * every project have nothing to decide and stay out (decision 2026-10-22). Sorted by last name,
 * case- and accent-insensitively.
 */
export const projectGuests = (members: ProjectMember[]): ProjectMember[] =>
  members.filter((member) => member.scope === 'PROJECT').sort(byLastName);
